import { useCallback, useEffect, useState } from 'react';
import { isBefore } from 'date-fns';
import { useGrfqBboService } from '#/unified-rfqs/contexts/grfq-bbo';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';

import { GrfqQuote, GrfqQuotes } from '#/unified-rfqs/entities/grfq/domain';
import { exceptionLogger, logException } from '@paradigm/logging/src/logging';
import { TradeMatchData } from './types';
import useRecentTradesListener from './useRecentTradesListener';
import { isBetterMark, isBetterPrice, isRecentTrade } from './shared';
import { OnQuoteMatchFound } from './ob-quote-matching-provider';

interface GrfqQuoteNotification extends GrfqQuote {
  notified?: boolean;
}
export default function useOnQuoteMatchedTrade(
  notifyQuoteFound: OnQuoteMatchFound,
) {
  const grfqBboService = useGrfqBboService();
  const grfqService = useGrfqService();

  const [matchedQuotes, setMatchedQuotes] = useState<
    Map<string | number, GrfqQuoteNotification>
  >(new Map());
  const [notifiedTrades, setNotifiedTrades] = useState<Set<string | number>>(
    new Set(),
  );
  const recentTradesWithObs = useRecentTradesListener();

  const onMarkQuoteNotified = useCallback(
    (tradeId: string | number) => {
      const quote = matchedQuotes.get(tradeId);
      if (quote == null) return;
      const newMap = matchedQuotes.set(tradeId, {
        ...quote,
        notified: true,
      });
      setMatchedQuotes(newMap);
    },
    [matchedQuotes],
  );

  const isQuoteSpreadBetter = useCallback(
    async (
      matchedTrade: TradeMatchData,
      quote: GrfqQuote,
    ): Promise<boolean> => {
      try {
        const resp = await grfqBboService.getGrfqBbo(matchedTrade.obId);
        if (!resp.ok) return false;
        return isBetterMark(matchedTrade, quote, resp.data.mark_price);
      } catch (error) {
        logException('Failed to get grfq bbo when checking quote match', error);
        return false;
      }
    },
    [grfqBboService],
  );

  /**
   * Iterate through list of quotes and notify once if a match has been found.
   * A quote matches a trade if:
   * - Price is better
   * - Spread from mark is better
   */
  const checkMatchOnQuoteList = useCallback(
    async (trade: TradeMatchData, quotes: readonly GrfqQuote[]) => {
      const { tradeId } = trade;
      for (const quote of quotes) {
        if (!isBetterPrice(trade, quote)) continue;
        // Notify only quotes created after trade
        if (isBefore(quote.created, trade.tradedAt)) continue;
        // Avoid notifying user own quotes
        if (quote.maker != null) continue;

        if (notifiedTrades.has(tradeId)) continue;

        const isBetterSpreadFromMark = await isQuoteSpreadBetter(trade, quote);
        if (!isBetterSpreadFromMark) continue;

        setMatchedQuotes((prev) => prev.set(tradeId, quote));
        setNotifiedTrades((prev) => prev.add(tradeId));
        notifyQuoteFound(trade, quote);
      }
    },
    [isQuoteSpreadBetter, notifiedTrades, notifyQuoteFound],
  );

  const onCheckQuoteMatchesRecentTrade = useCallback(
    (trade: TradeMatchData) => (quotes: GrfqQuotes) => {
      const { tradeId, side: tradeSide, tradedAt } = trade;
      if (!isRecentTrade(tradedAt)) return;
      if (notifiedTrades.has(tradeId)) return;

      const quotesData = tradeSide === 'BUY' ? quotes.asks : quotes.bids;
      checkMatchOnQuoteList(trade, quotesData).catch(
        exceptionLogger('Unexpected error in quote match'),
      );
    },
    [checkMatchOnQuoteList, notifiedTrades],
  );

  /**
   * This effect subscribes to OB quotes for each trade that matches an OB.
   * In the quotes handler, we invoke the callback if the price is better.
   */
  useEffect(() => {
    const unsubscribeHandlers = recentTradesWithObs.map((tradeData) => {
      return grfqService.subscribeRfqQuotes(
        tradeData.obId,
        onCheckQuoteMatchesRecentTrade(tradeData),
      );
    });

    return () => unsubscribeHandlers.forEach((handler) => handler());
  }, [grfqService, onCheckQuoteMatchesRecentTrade, recentTradesWithObs]);

  return { matchedQuotes, onMarkQuoteNotified };
}
